import { z } from "zod";
import type { Id } from "../lib/api";
import {
  ALERT_KINDS,
  ALERT_STATUSES,
  type AlertKind,
  type AlertStatus,
  type ChangeType,
} from "../../convex/model/enums";
import {
  CHANGE_TYPE_OPTIONS,
  changeTypeDisplayName,
  changeTypeFromInput,
  matchesQuery,
  type LegislationSummary,
} from "./legislation";
import { parseOneOf, type OneOfParseError } from "./guards";
import { err, ok, type Result } from "./result";
import { cleanOptionalText } from "./text";
import { parseOptionalDateInputAtUtcHour } from "./time";
import { formatZodIssues } from "./zod";

export { changeTypeDisplayName, changeTypeFromInput };
export * from "../../convex/model/alerts";

export type AlertStatusFilter = AlertStatus | "all";

export type AlertListQueryArgs = {
  readonly status?: AlertStatus;
  readonly limit?: number;
};

export type AlertUpdateLogActionView = {
  readonly disabled: boolean;
  readonly buttonLabel: string;
};

export type AlertUpdateLogRecordState<AlertId extends string = string> = {
  readonly busyId: AlertId | null;
  readonly error: string | null;
};

export const emptyAlertUpdateLogRecordState = <AlertId extends string = string>(): AlertUpdateLogRecordState<AlertId> => ({
  busyId: null,
  error: null,
});

export type AlertUpdateLogRecordAction<AlertId extends string = string> =
  | { readonly type: "started"; readonly alertId: AlertId }
  | { readonly type: "succeeded" }
  | { readonly type: "failed"; readonly error: string };

export const nextAlertUpdateLogRecordState = <AlertId extends string>(
  _current: AlertUpdateLogRecordState<AlertId>,
  action: AlertUpdateLogRecordAction<AlertId>,
): AlertUpdateLogRecordState<AlertId> => {
  switch (action.type) {
    case "started":
      return { busyId: action.alertId, error: null };
    case "succeeded":
      return { busyId: null, error: null };
    case "failed":
      return { busyId: null, error: action.error };
  }
};

export type AlertStatusFilterButtonInput = {
  readonly current: AlertStatusFilter;
  readonly option: AlertStatusFilter;
};

export type AlertStartActionView = {
  readonly disabled: boolean;
  readonly buttonLabel: "Start actioning";
  readonly nextStatus: "inProgress";
};

export const ALERT_KIND_OPTIONS = [
  "amending",
  "principal",
  "bill",
  "proclamation",
  "manual",
] as const satisfies readonly AlertKind[];

export const ALERT_STATUS_FILTER_OPTIONS = [
  "new",
  "inProgress",
  "deferred",
  "actioned",
  "all",
] as const satisfies readonly AlertStatusFilter[];

export const ALERT_CHANGE_TYPE_OPTIONS = CHANGE_TYPE_OPTIONS;

const ALERT_STATUS_LABELS = {
  new: "New",
  inProgress: "In progress",
  actioned: "Actioned",
  deferred: "Deferred",
} satisfies Record<AlertStatus, string>;

const ALERT_STATUS_TONE_CLASSES = {
  new: "bg-amber-50 text-amber-700 ring-amber-200",
  inProgress: "bg-sky-50 text-sky-700 ring-sky-200",
  actioned: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  deferred: "bg-slate-50 text-slate-600 ring-slate-200",
} satisfies Record<AlertStatus, string>;

export const alertStatusDisplayName = (status: AlertStatus): string =>
  ALERT_STATUS_LABELS[status];

export const alertStatusToneClass = (status: AlertStatus): string =>
  ALERT_STATUS_TONE_CLASSES[status];

export const alertStatusFilterDisplayName = (filter: AlertStatusFilter): string =>
  filter === "all" ? "All" : alertStatusDisplayName(filter);

const ALERT_STATUS_FILTER_BUTTON_CLASSES = {
  selected: "bg-[var(--accent-soft)] text-[var(--accent-dark)]",
  idle: "text-slate-500 hover:bg-slate-50",
} satisfies Record<"selected" | "idle", string>;

export const alertStatusFilterButtonClass = ({
  current,
  option,
}: AlertStatusFilterButtonInput): string =>
  ALERT_STATUS_FILTER_BUTTON_CLASSES[current === option ? "selected" : "idle"];

export const alertStartActionView = (status: AlertStatus): AlertStartActionView => ({
  disabled: status === "inProgress",
  buttonLabel: "Start actioning",
  nextStatus: "inProgress",
});

export const alertListQueryArgs = (
  filter: AlertStatusFilter,
  limit = 80,
): AlertListQueryArgs =>
  filter === "all" ? { limit } : { status: filter, limit };

export const alertUpdateLogActionView = ({
  hasUpdateLog,
  busy,
}: {
  readonly hasUpdateLog: boolean;
  readonly busy: boolean;
}): AlertUpdateLogActionView => ({
  disabled: hasUpdateLog || busy,
  buttonLabel: hasUpdateLog
    ? "Update log recorded"
    : busy
      ? "Recording..."
      : "Record update log",
});

export type AlertStatusUpdateCommand<AlertId extends string = string> = {
  readonly id: AlertId;
  readonly status: AlertStatus;
};

export const alertDeferCommand = <AlertId extends string>(
  id: AlertId,
): AlertStatusUpdateCommand<AlertId> => ({
  id,
  status: "deferred",
});

export const alertStartStatusCommand = <AlertId extends string>(
  id: AlertId,
  currentStatus: AlertStatus,
): AlertStatusUpdateCommand<AlertId> | null => {
  const view = alertStartActionView(currentStatus);
  return view.disabled ? null : { id, status: view.nextStatus };
};

export const alertKindDisplayName = (kind: AlertKind): string => {
  const labels: Record<AlertKind, string> = {
    principal: "Principal",
    amending: "Amending",
    bill: "Bill",
    proclamation: "Proclamation",
    manual: "Manual",
  };
  return labels[kind];
};

export const alertStatusInputResult = (value: string): Result<AlertStatus, OneOfParseError> =>
  parseOneOf(ALERT_STATUSES, value);

export const alertStatusFromInput = (value: string, fallback: AlertStatus): AlertStatus => {
  const parsed = alertStatusInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

/**
 * Normalized representation of an inbound legislative alert (from Intertek
 * Inform / LawLex). The raw email/HTML parsing is an AI/boundary concern; this
 * module models the *structured* result and the pure matching against what
 * clients actually subscribe to — producing the editor's worklist.
 */

export const alertItemSchema = z.object({
  amendingLegislation: z.string().min(1),
  // References (or titles) of the legislation affected by this amendment.
  affectedReferences: z.array(z.string().min(1)),
  commencementDate: z.number().nullable(),
  changeType: z.enum(["amended", "insertion", "repeal"]),
});

export type AlertItem = {
  readonly amendingLegislation: string;
  readonly affectedReferences: readonly string[];
  readonly commencementDate: number | null;
  readonly changeType: ChangeType;
};

export type AlertItemsParseError = {
  readonly kind: "invalidAlertItems";
  readonly message: string;
};

export const alertItemsParseErrorMessage = (error: AlertItemsParseError): string =>
  "Alert items did not match the expected schema: " + error.message;

export const parseAlertItems = (
  raw: unknown,
): Result<AlertItem[], AlertItemsParseError> => {
  const parsed = z.array(alertItemSchema).safeParse(raw);
  return parsed.success
    ? ok(parsed.data)
    : err({ kind: "invalidAlertItems", message: formatZodIssues(parsed.error.issues) });
};


export type WorklistEntry = {
  readonly amendingLegislation: string;
  readonly affectedReference: string;
  readonly changeType: ChangeType;
  readonly commencementDate: number | null;
  /** True once the commencement date has arrived (only commenced changes are actioned). */
  readonly commenced: boolean;
};

/**
 * Turn alert items into a flat worklist of (amendment × affected legislation)
 * pairs, keeping only those that touch legislation in the catalogue. Mirrors
 * the manual step of finding the `[**]` "in your alert profile" entries.
 */
export const buildWorklist = (
  items: readonly AlertItem[],
  knownReferences: ReadonlySet<string>,
  now: number,
): WorklistEntry[] => {
  const out: WorklistEntry[] = [];
  for (const item of items) {
    for (const ref of item.affectedReferences) {
      if (!knownReferences.has(ref)) continue;
      out.push({
        amendingLegislation: item.amendingLegislation,
        affectedReference: ref,
        changeType: item.changeType,
        commencementDate: item.commencementDate,
        commenced: item.commencementDate !== null && item.commencementDate <= now,
      });
    }
  }
  return out;
};

/** Only commenced changes are actioned; future ones are filed for later. */
export const commencedEntries = (entries: readonly WorklistEntry[]): WorklistEntry[] =>
  entries.filter((e) => e.commenced);

export type AlertLegislationOption<LegislationId extends string = Id<"legislation">> = {
  readonly _id: LegislationId;
  readonly title: string;
  readonly reference: string;
};

export type AlertLegislationSearchRow<LegislationId extends string = Id<"legislation">> =
  LegislationSummary & {
    readonly _id: LegislationId;
  };

export type AlertLawMatchesInput<
  LegislationId extends string,
  Row extends AlertLegislationSearchRow<LegislationId>,
> = {
  readonly legislation: readonly Row[];
  readonly query: string;
  readonly limit?: number;
};

export const DEFAULT_ALERT_LAW_MATCH_LIMIT = 20;

const normalizedAlertLawMatchLimit = (limit: number | undefined): number => {
  if (limit === undefined || !Number.isFinite(limit)) return DEFAULT_ALERT_LAW_MATCH_LIMIT;
  return Math.max(0, Math.floor(limit));
};

export const alertLawMatches = <
  LegislationId extends string,
  Row extends AlertLegislationSearchRow<LegislationId>,
>({
  legislation,
  query,
  limit,
}: AlertLawMatchesInput<LegislationId, Row>): Row[] =>
  legislation
    .filter((law) => matchesQuery(law, query))
    .slice(0, normalizedAlertLawMatchLimit(limit));

export const shouldShowAlertLawMatches = (query: string): boolean =>
  query.trim() !== "";

export const selectedAlertLaw = <
  LegislationId extends string,
  Row extends { readonly _id: LegislationId },
>({
  legislation,
  legislationId,
}: {
  readonly legislation: readonly Row[];
  readonly legislationId: LegislationId | null;
}): Row | null =>
  legislationId === null ? null : legislation.find((law) => law._id === legislationId) ?? null;

export type AlertCaptureDraft<LegislationId extends string = Id<"legislation">> = {
  readonly kind: AlertKind;
  readonly changeType: ChangeType;
  readonly linkedLegislation: AlertLegislationOption<LegislationId> | null;
  readonly lawSearch: string;
  readonly amendingLegislation: string;
  readonly commencementDate: string;
  readonly summary: string;
  readonly notes: string;
};

export type AlertCaptureFormState<LegislationId extends string = Id<"legislation">> = {
  readonly kind: AlertKind;
  readonly changeType: ChangeType;
  readonly legislationId: LegislationId | null;
  readonly lawSearch: string;
  readonly amendingLegislation: string;
  readonly commencementDate: string;
  readonly summary: string;
  readonly notes: string;
};

export type AlertCaptureDraftFromFormStateInput<LegislationId extends string = Id<"legislation">> = {
  readonly state: AlertCaptureFormState<LegislationId>;
  readonly linkedLegislation: AlertLegislationOption<LegislationId> | null;
};

export const emptyAlertCaptureFormState = (): AlertCaptureFormState => ({
  kind: "amending",
  changeType: "amended",
  legislationId: null,
  lawSearch: "",
  amendingLegislation: "",
  commencementDate: "",
  summary: "",
  notes: "",
});

export type AlertCaptureFormAction<LegislationId extends string = Id<"legislation">> =
  | { readonly type: "kindInput"; readonly value: string }
  | { readonly type: "changeTypeInput"; readonly value: string }
  | { readonly type: "lawSearchInput"; readonly value: string }
  | { readonly type: "selectLegislation"; readonly legislation: Pick<AlertLegislationOption<LegislationId>, "_id" | "title"> }
  | { readonly type: "amendingLegislationInput"; readonly value: string }
  | { readonly type: "commencementDateInput"; readonly value: string }
  | { readonly type: "summaryInput"; readonly value: string }
  | { readonly type: "notesInput"; readonly value: string };

export const nextAlertCaptureFormState = <LegislationId extends string>(
  current: AlertCaptureFormState<LegislationId>,
  action: AlertCaptureFormAction<LegislationId>,
): AlertCaptureFormState<LegislationId> => {
  switch (action.type) {
    case "kindInput":
      return { ...current, kind: alertKindFromInput(action.value, current.kind) };
    case "changeTypeInput":
      return { ...current, changeType: changeTypeFromInput(action.value, current.changeType) };
    case "lawSearchInput":
      return { ...current, lawSearch: action.value, legislationId: null };
    case "selectLegislation":
      return { ...current, legislationId: action.legislation._id, lawSearch: action.legislation.title };
    case "amendingLegislationInput":
      return { ...current, amendingLegislation: action.value };
    case "commencementDateInput":
      return { ...current, commencementDate: action.value };
    case "summaryInput":
      return { ...current, summary: action.value };
    case "notesInput":
      return { ...current, notes: action.value };
  }
};

export const alertCaptureDraftFromFormState = <LegislationId extends string>({
  state,
  linkedLegislation,
}: AlertCaptureDraftFromFormStateInput<LegislationId>): AlertCaptureDraft<LegislationId> => ({
  kind: state.kind,
  changeType: state.changeType,
  linkedLegislation,
  lawSearch: state.lawSearch,
  amendingLegislation: state.amendingLegislation,
  commencementDate: state.commencementDate,
  summary: state.summary,
  notes: state.notes,
});

export type AlertCaptureTouchedDraft = Pick<
  AlertCaptureDraft,
  "lawSearch" | "amendingLegislation" | "commencementDate" | "summary" | "notes"
>;

export const isAlertCaptureDraftTouched = (draft: AlertCaptureTouchedDraft): boolean =>
  draft.lawSearch.trim() !== "" ||
  draft.amendingLegislation.trim() !== "" ||
  draft.commencementDate.trim() !== "" ||
  draft.summary.trim() !== "" ||
  draft.notes.trim() !== "";

export type PreparedAlertCapture = {
  readonly source: "Manual";
  readonly kind: AlertKind;
  readonly legislationId: Id<"legislation"> | null;
  readonly legislationReference?: string;
  readonly legislationTitle?: string;
  readonly amendingLegislation: string;
  readonly commencementDate: number | null;
  readonly changeType: ChangeType;
  readonly summary?: string;
  readonly notes?: string;
};

export type AlertCaptureError =
  | { readonly kind: "missingAmendingLegislation" }
  | { readonly kind: "invalidCommencementDate" };

export type AlertCaptureView = {
  readonly prepared: Result<PreparedAlertCapture, AlertCaptureError>;
  readonly touched: boolean;
  readonly problem: string | null;
  readonly canSubmit: boolean;
};

export const alertCaptureErrorMessage = (error: AlertCaptureError): string => {
  switch (error.kind) {
    case "missingAmendingLegislation":
      return "Amending legislation is required.";
    case "invalidCommencementDate":
      return "Commencement date must be a valid date.";
  }
};

export const alertKindInputResult = (value: string): Result<AlertKind, OneOfParseError> =>
  parseOneOf(ALERT_KINDS, value);

export const alertKindFromInput = (value: string, fallback: AlertKind): AlertKind => {
  const parsed = alertKindInputResult(value);
  return parsed.ok ? parsed.value : fallback;
};

export const alertCaptureView = (
  draft: AlertCaptureDraft,
): AlertCaptureView => {
  const prepared = prepareAlertCapture(draft);
  const touched = isAlertCaptureDraftTouched(draft);
  return {
    prepared,
    touched,
    problem: !prepared.ok && touched ? alertCaptureErrorMessage(prepared.error) : null,
    canSubmit: prepared.ok,
  };
};

export const prepareAlertCapture = (
  draft: AlertCaptureDraft,
): Result<PreparedAlertCapture, AlertCaptureError> => {
  const amendingLegislation = draft.amendingLegislation.trim();
  if (amendingLegislation === "") return err({ kind: "missingAmendingLegislation" });

  const commencementDate = parseOptionalInputDate(draft.commencementDate);
  if (!commencementDate.ok) return commencementDate;

  const lawSearchTitle = cleanOptionalText(draft.lawSearch);
  const summary = cleanOptionalText(draft.summary);
  const notes = cleanOptionalText(draft.notes);

  return ok({
    source: "Manual",
    kind: draft.kind,
    legislationId: draft.linkedLegislation?._id ?? null,
    ...(draft.linkedLegislation === null
      ? lawSearchTitle === undefined ? {} : { legislationTitle: lawSearchTitle }
      : {
          legislationReference: draft.linkedLegislation.reference,
          legislationTitle: draft.linkedLegislation.title,
        }),
    amendingLegislation,
    commencementDate: commencementDate.value,
    changeType: draft.changeType,
    ...(summary === undefined ? {} : { summary }),
    ...(notes === undefined ? {} : { notes }),
  });
};


const parseOptionalInputDate = (value: string): Result<number | null, AlertCaptureError> => {
  const parsed = parseOptionalDateInputAtUtcHour(value, 0);
  return parsed.ok ? ok(parsed.value) : err({ kind: "invalidCommencementDate" });
};

